import { useCallback, useEffect, useState } from "react";

import { Source } from "Constant/source";

import { createSource } from "helpers/Sources/createSource";

import SwalAlertComp from "components/SwalAlert";

import {
    CRM_SOURCE_TYPE_CODES,
    GENERIC_HOST,
    SFTP_SOURCE_TYPES,
    WEB_SOURCE_TYPES,
} from "../SourceCreation.constants";
import {
    appendDomainHttps,
    findFormErrors,
    findRequiredFieldErrors,
} from "../SourceCreation.helpers";
import useAppCapiSource from "./useAppCapiSource";
import useHubSpotSource from "./useHubSpotSource";
import useSftpSource from "./useSftpSource";
import useSourceForm from "./useSourceForm";
import useSourceMappingView from "./useSourceMappingView";
import useSpreadsheetSource from "./useSpreadsheetSource";
import useUsageTypes from "./useUsageTypes";
import useZohoSource from "./useZohoSource";

const noop = () => {};

/**
 * Host-agnostic source creation state machine.
 *
 * @param {object} params
 * @param {object} params.language
 * @param {Array} params.sourceTypes Raw source types from the store.
 * @param {(info: { sourceId: any, sourceType: string, sourceTypeId?: any, isTrackerHandledByChild?: boolean }) => (void|Promise<void>)} params.onCreated
 *   Called once per created source, after any mapping step has completed.
 * @param {() => void} params.onCancel Called when the user clicks Close.
 * @param {{ addToTracker?: Function, getTracker?: Function, handleGetSources?: Function, trackerId?: any }} [params.hostActions]
 *   Passed through unchanged to `FileUploadComponent` and `source_types/*`.
 * @param {string} [params.initialUploadName] Pre-filled name for the upload flow.
 * @param {string} [params.initialSourceType] Source type code selected on mount.
 * @param {boolean} [params.isTypeLocked] Prevents changing `initialSourceType`.
 */
const useSourceCreation = ({
    language,
    sourceTypes,
    onCreated = noop,
    onCancel = noop,
    hostActions = {},
    initialUploadName = "",
    initialSourceType,
    isTypeLocked = false,
}) => {
    const form = useSourceForm();
    const { sourceType } = form;

    useEffect(() => {
        if (initialSourceType) form.setField("source_type", initialSourceType);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSourceType]);

    const usageTypes = useUsageTypes({ sourceType });
    const { usage, setUsage, crmUsageIds } = usageTypes;

    const mapping = useSourceMappingView();

    const zoho = useZohoSource({
        sourceType,
        language,
        crmUsageIds,
        onCreated,
    });
    const hubspot = useHubSpotSource({ language, onCreated });
    const appCapi = useAppCapiSource({
        sourceType,
        language,
        usage,
        onCreated,
    });
    const sftp = useSftpSource({
        sourceType,
        language,
        usage,
        leadMappings: zoho.leadMappings,
        openMapping: mapping.openMapping,
    });
    const spreadsheet = useSpreadsheetSource({
        sourceType,
        language,
        usage,
        openMapping: mapping.openMapping,
    });

    const [loading, setLoading] = useState(false);

    // Upload flow
    const [uploadName, setUploadName] = useState("");
    const [uploadError, setUploadError] = useState("");
    const [uploadFile, setUploadFile] = useState(null);
    const [isUploadTriggered, setIsUploadTriggered] = useState(false);
    const [isUploadLoading, setIsUploadLoading] = useState(false);

    useEffect(() => {
        if (initialUploadName) setUploadName(initialUploadName);
    }, [initialUploadName]);

    useEffect(() => {
        setIsUploadLoading(false);
    }, [sourceType]);

    // CRM / MMP children that moved past their first step hide the type select.
    const [typeSelectBlockers, setTypeSelectBlockers] = useState({});
    const setTypeSelectBlocked = useCallback((code, isBlocked) => {
        setTypeSelectBlockers((prev) =>
            prev[code] === isBlocked ? prev : { ...prev, [code]: isBlocked },
        );
    }, []);

    /**
     * Whether the user may still go back and pick another type. CRM / MMP
     * children that moved past their first step lock the current type.
     */
    const canChangeType =
        !hubspot.isConnected &&
        !zoho.hasFields &&
        !CRM_SOURCE_TYPE_CODES.some((code) => typeSelectBlockers[code]);

    /**
     * Primary footer actions registered by child forms that no longer render
     * their own buttons (legacy CRM components, CrmSourceMappingArea). Keyed
     * by stage so the mapping-stage Save takes precedence over the
     * credentials-stage action.
     */
    const [footerActions, setFooterActions] = useState({});
    const registerFooterAction = useCallback((key, action) => {
        setFooterActions((prev) => {
            if (prev[key] === action) return prev;
            if (action === null && !(key in prev)) return prev;
            const next = { ...prev };
            if (action === null) delete next[key];
            else next[key] = action;
            return next;
        });
    }, []);
    const footerAction = footerActions.mapping ?? footerActions.form ?? null;

    const selectSourceType = useCallback(
        (code) => {
            form.setField("source_type", code);
            form.setFormErrors({});
            setFooterActions({});
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [form.setField, form.setFormErrors],
    );

    /**
     * Clears the selected type and every per-type form state. Used when the
     * host steps back to the type catalog so a re-entered form starts clean.
     */
    const resetFormState = () => {
        form.resetForm();
        setUsage([]);
        mapping.closeMapping();
        zoho.reset();
        hubspot.reset();
        appCapi.reset();
        sftp.reset();
        spreadsheet.reset();
        setUploadName(initialUploadName ?? "");
        setUploadError("");
        setUploadFile(null);
        setIsUploadTriggered(false);
        setIsUploadLoading(false);
        setTypeSelectBlockers({});
        setFooterActions({});
        setLoading(false);
    };

    const failedTitle = language?.delete_swal_modal?.failed ?? "Failed";

    const handleUploadNameChange = (event) => {
        const { value } = event.target;
        setUploadName(value);
        if (uploadError && value !== "") setUploadError("");
    };

    const triggerUpload = () => {
        if (!uploadName) {
            setUploadError(
                language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!",
            );
            return;
        }
        setUploadError("");
        setIsUploadTriggered(true);
    };

    const createGenericSource = async (payload) => {
        setLoading(true);
        try {
            const response = await createSource(payload);
            if (!response.status) {
                SwalAlertComp({
                    title: failedTitle,
                    message: response.message && response.message,
                    icon: "error",
                });
                return;
            }
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message && response.message,
                icon: "success",
                timer: 3000,
            });
            setUsage([]);
            form.setSubdomain("");
            form.setUseGenericDomain(true);
            await onCreated({
                sourceId: response?.data?.id,
                sourceType: payload.source_type,
                sourceTypeId: response?.data?.source_type_id,
            });
        } finally {
            setLoading(false);
        }
    };

    const createWebSource = () => {
        const { form: values, subdomain, useGenericDomain, aliases } = form;
        const errors = useGenericDomain
            ? findRequiredFieldErrors({ form: values, language })
            : findFormErrors({ form: values, language, subdomain });
        if (Object.keys(errors).length > 0) {
            form.setFormErrors(errors);
            return;
        }
        createGenericSource({
            source_type: values.source_type,
            domain: appendDomainHttps(values.domain),
            host: useGenericDomain
                ? GENERIC_HOST
                : `${values.host}.${subdomain}`,
            isgeneric: useGenericDomain ? 1 : 0,
            alias: aliases,
            usage_types: usage,
        });
    };

    const handleCreateSource = () => {
        if (loading) return;
        if (sourceType === Source.upload) {
            triggerUpload();
        } else if (WEB_SOURCE_TYPES.includes(sourceType)) {
            createWebSource();
        } else {
            const errors = findRequiredFieldErrors({
                form: form.form,
                language,
            });
            if (Object.keys(errors).length > 0) {
                form.setFormErrors(errors);
                return;
            }
            createGenericSource({
                source_type: sourceType,
                domain: form.form.domain,
                host: form.form.domain,
                usage_types: usage,
            });
        }
    };

    /** Success callback for the post-create `SourceMappingArea`. */
    const handleMappingSuccess = async (response) => {
        SwalAlertComp({
            title: language?.delete_swal_modal?.success ?? "Success",
            message: response.message && response.message,
            icon: "success",
            timer: 3000,
        });
        const { mappingSourceId, mappingSourceType, mappingSourceTypeId } =
            mapping;

        if (SFTP_SOURCE_TYPES.includes(mappingSourceType)) {
            sftp.reset();
        } else if (mappingSourceType === Source.spreadsheet) {
            spreadsheet.reset();
        } else if (mappingSourceType === Source.upload) {
            setUploadFile(null);
            setIsUploadTriggered(false);
        }
        setUsage([]);
        mapping.closeMapping();
        setLoading(false);

        await onCreated({
            sourceId: mappingSourceId,
            sourceType: mappingSourceType,
            sourceTypeId: mappingSourceTypeId,
        });
    };

    /**
     * `source_types/*` components call `handleClose(sourceId)` on success and
     * `handleClose()` on cancel. They attach to the tracker themselves when
     * `hostActions.getTracker` is provided.
     */
    const handleCrmClose = (sourceId) => {
        if (sourceId) {
            onCreated({
                sourceId,
                sourceType,
                isTrackerHandledByChild: !!hostActions.getTracker,
            });
        } else {
            onCancel();
        }
    };

    return {
        language,
        form,
        usage: usageTypes,
        mapping,
        zoho,
        hubspot,
        appCapi,
        sftp,
        spreadsheet,
        upload: {
            name: uploadName,
            error: uploadError,
            file: uploadFile,
            setFile: setUploadFile,
            isTriggered: isUploadTriggered,
            setIsTriggered: setIsUploadTriggered,
            isLoading: isUploadLoading,
            setIsLoading: setIsUploadLoading,
            handleNameChange: handleUploadNameChange,
        },
        loading,
        setLoading,
        sourceTypes,
        canChangeType,
        selectSourceType,
        resetFormState,
        isTypeLocked: isTypeLocked && !!initialSourceType,
        setTypeSelectBlocked,
        footerAction,
        registerFooterAction,
        handleCreateSource,
        handleMappingSuccess,
        handleCrmClose,
        hostActions,
        onCancel,
    };
};

export default useSourceCreation;
